import React from 'react';
import {
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { FaExternalLinkAlt, FaPen, FaTrash } from "react-icons/fa";
import ResponsiveTable  from "./../../ResponsiveTable";
import {OverviewSubheader} from "./../../layouts";
import {
  Buttons,
} from "./../../../components";
import { PortalizedModalContext } from "./../../../components/PortalizedModal";
import { formatDate } from '../../../utils/datetime';
import { liveEventApi } from './api';
import StatusUpdateButton from './StatusUpdateButton';
import { useSessionContext } from "./../../../contexts/SessionContext";

export default function List({ companyId, offerId }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { isPermitted, ACTIONS } = useSessionContext();
  const deleteModal = React.useContext( PortalizedModalContext );
  const { isLoading, error, data: liveEvents } = useQuery(['live-events-list', offerId], () => {
    return liveEventApi.list(offerId);
  });

  const canEdit = isPermitted(ACTIONS.WRITE_UPDATE_LIVE_EVENTS, companyId)

  const openEditorModal = React.useCallback(
    (action, liveEvent) => {
      navigate(
        action === "edit" ? `live-events/${liveEvent.id}/edit` : `live-events/new`,
        {
          state: {
            backgroundLocation: location
          }
        }
      )
    },
    [navigate, location]
  )

  if(isLoading){
    return "Loading live events...";
  }

  if(error){
    return <div className='text-center text-danger montserrat weight-500 fs-8 mt-4'>Something went wrong please try again after some time.</div>
  }

  const deleteHandler  = (id) => {
    deleteModal.setModalContent(
      null,
      <div>
        <div className="px-3">
          <p>Are you sure you want to delete the live event?</p>
        </div>
        <div className="p-3 text-right">
          <button
            className="btn btn-dark rounded-0 px-3"
            onClick={() => {
              deleteLiveEvent(id);
              deleteModal.closeModal();
            }}
          >
            Yes, delete live event.
          </button>
        </div>
      </div>
    );
    deleteModal.openModal();
  }

  const deleteLiveEvent = (id) => {
    const liveEvent = liveEvents.find((e) => e.id === id);

    if(!liveEvent){
      return;
    }

    liveEventApi.destroy(liveEvent.id).then(() => {
      queryClient.invalidateQueries(['live-events-list', offerId]);
      toast.success(`${liveEvent.title} deleted.`);
    }).catch(function (error) {
      toast.error(error);
    })
  };

  const columns = [
    {
      Header: 'Title',
      accessor: 'title'
    },
    {
      Header: 'Status',
      accessor: 'status'
    },
     {
      Header: 'Scheduled Time',
      accessor: 'scheduled_time',
      Cell: ({value}) => value ? formatDate(value) : ''
    },
    {
      Header: '',
      accessor: 'id',
      Cell: ({value, row}) => (
        <div className="table-action-items-container justify-content-end">
          { canEdit && <StatusUpdateButton liveEvent={row.original}/> }
          <a href={row.original.youtube_url} title='Open Event' className="btn btn-sm p-0 ps-2 action-item cursor-pointer" target="_blank" rel="noopener noreferrer"><FaExternalLinkAlt className='fs-12x color-black'/></a>
          { canEdit && 
            <button className="btn btn-sm p-0 ps-2 action-item cursor-pointer" title='Edit Event' onClick={ ()=> { openEditorModal("edit", row.original) } }>
              <FaPen className='fs-14 color-black'/>
            </button>
          }
          { canEdit &&
            <button className="btn btn-sm p-0 ps-2 action-item cursor-pointer" title='Delete Event' onClick={ ()=> { deleteHandler(value) } }>
              <FaTrash className='fs-14 color-black'/>
            </button>
          }
        </div>
      )
    }
  ];

  return(
    <>
      <OverviewSubheader left='Live Events' right={canEdit && <Buttons.TextButton title='New Live Event' onClick={ () => openEditorModal("new") } /> } tooltipDesc="All past and upcoming Live Events for your offer are listed here. Spaced Ventures admins set up and manage the Live Events. Please reach out to Spaced Ventures if you are interested in doing a Live Event." />
      <div className="action-table">
        <ResponsiveTable title="Live Events" columns={columns} data={liveEvents}/>
      </div>
    </>
  );
}
